import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { arrayField, createBlock, headingField, htmlField } from "./blockBuilder";
import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import { MediaType } from "@/views/blocks/PageBlock.interface";
import { ICON_API_BASE_URL, getStockPhotoUrl } from "@/apps/page/services/page.service";
import {
    BorderedBar,
    FullWidthTabsWithUnderline,
    TabsInPills,
    TabsWithUnderlineAndIcons,
    WithTabs,
} from "@/views/blocks/tabs/tabs";
import type {
    BorderedBarProps,
    FullWidthTabsWithUnderlineProps,
    TabsInPillsProps,
    TabsWithUnderlineAndIconsProps,
    WithTabsProps,
} from "@/views/blocks/tabs/tabs";
import { useFieldConfig } from "./useFieldConfig";

// Import the default video
import defaultVideoUrl from "@/views/blocks/shared/videos/default-video.mp4";

export function useTabs() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const borderedBarBlock = computed<BlockDef>(() =>
        createBlock<BorderedBarProps>({
            typeId: "BorderedBar",
            type: "tabs",
            name: t("page.blocks.tabs.borderedBar"),
            component: BorderedBar,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Community Hub", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Connect, collaborate, and grow together in our vibrant community platform designed for meaningful interactions.",
                    translations.value.text
                )
            )
            .withArrayField(
                "tabs",
                arrayField(
                    translations.value.tab,
                    "tabs",
                    {
                        label: {
                            label: translations.value.label,
                            type: "text",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        mediaType: {
                            label: translations.value.mediaType,
                            type: "media",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "media",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "media",
                        },
                        videoSrc: {
                            label: translations.value.videoSrc,
                            type: "media",
                        },
                        videoAutoplay: {
                            label: translations.value.videoAutoplay,
                            type: "media",
                        },
                        videoControls: {
                            label: translations.value.videoControls,
                            type: "media",
                        },
                        videoLoop: {
                            label: translations.value.videoLoop,
                            type: "media",
                        },
                        iframeSrc: {
                            label: translations.value.iframeSrc,
                            type: "media",
                        },
                    },
                    [
                        {
                            label: "Communities",
                            title: "Join Communities",
                            description:
                                "Connect with like-minded individuals and build lasting relationships in our diverse community groups.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "communities",
                                "close-up-happy-people-posing-together"
                            ),
                            imageAlt: "Community gathering and networking",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Events",
                            title: "Upcoming Events",
                            description:
                                "Discover and attend exciting events, workshops, and meetups happening in your area and online.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "events-and-tickets",
                                "people-taking-part-business-event"
                            ),
                            imageAlt: "Community events and gatherings",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Learning",
                            title: "Learning Hub",
                            description:
                                "Access courses, webinars, and educational resources to expand your knowledge and skills.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "courses",
                                "online-classes-with-student-pile-books"
                            ),
                            imageAlt: "Online learning and education",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Resources",
                            title: "Digital Resources",
                            description:
                                "Explore our library of e-books, guides, and tools to support your personal and professional growth.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl("e-books", "laptop-books-arrangement"),
                            imageAlt: "Digital resources and e-books",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        label: "New Tab",
                        title: "New Tab Title",
                        description: "New tab description",
                        mediaType: MediaType.IMAGE,
                        imageSrc: getStockPhotoUrl(
                            "communities",
                            "networking-concept-still-life-arrangement"
                        ),
                        imageAlt: "New tab image",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const fullWidthTabsWithUnderlineBlock = computed<BlockDef>(() =>
        createBlock<FullWidthTabsWithUnderlineProps>({
            typeId: "FullWidthTabsWithUnderline",
            type: "tabs",
            name: t("page.blocks.tabs.fullWidthTabsWithUnderline"),
            component: FullWidthTabsWithUnderline,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Professional Development Platform", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Advance your career with our comprehensive suite of professional development tools and resources",
                    translations.value.text
                )
            )
            .withArrayField(
                "tabs",
                arrayField(
                    translations.value.tab,
                    "tabs",
                    {
                        label: {
                            label: translations.value.label,
                            type: "text",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        mediaType: {
                            label: translations.value.mediaType,
                            type: "media",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "media",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "media",
                        },
                        videoSrc: {
                            label: translations.value.videoSrc,
                            type: "media",
                        },
                        videoAutoplay: {
                            label: translations.value.videoAutoplay,
                            type: "media",
                        },
                        videoControls: {
                            label: translations.value.videoControls,
                            type: "media",
                        },
                        videoLoop: {
                            label: translations.value.videoLoop,
                            type: "media",
                        },
                        iframeSrc: {
                            label: translations.value.iframeSrc,
                            type: "media",
                        },
                    },
                    [
                        {
                            label: "Courses",
                            title: "Online Courses",
                            description:
                                "Access industry-leading courses designed by experts to help you master new skills and advance your career.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "courses",
                                "student-listening-online-courses-e-learning-concept"
                            ),
                            imageAlt: "Online learning and courses",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Webinars",
                            title: "Live Webinars",
                            description:
                                "Join interactive webinars and workshops led by industry professionals and thought leaders.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl("webinars", "close-up-virtual-classroom"),
                            imageAlt: "Live webinars and online workshops",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Software",
                            title: "SaaS Tools",
                            description:
                                "Utilize powerful software tools and platforms to streamline your workflow and boost productivity.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl("licences-saas", "coding-man"),
                            imageAlt: "Software development and SaaS tools",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Library",
                            title: "Resource Library",
                            description:
                                "Browse our extensive collection of e-books, whitepapers, and industry reports to stay informed.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl("e-books", "tablet-books-arrangement"),
                            imageAlt: "Digital library and resources",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        label: "New Tab",
                        title: "New Tab Title",
                        description: "New tab description",
                        mediaType: MediaType.IMAGE,
                        imageSrc: getStockPhotoUrl("courses", "woman-learning-from-home"),
                        imageAlt: "New tab image",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const tabsInPillsBlock = computed<BlockDef>(() =>
        createBlock<TabsInPillsProps>({
            typeId: "TabsInPills",
            type: "tabs",
            name: t("page.blocks.tabs.tabsInPills"),
            component: TabsInPills,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Event Management Suite", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Everything you need to create, manage, and host successful events from start to finish.",
                    translations.value.text
                )
            )
            .withArrayField(
                "tabs",
                arrayField(
                    translations.value.tab,
                    "tabs",
                    {
                        label: {
                            label: translations.value.label,
                            type: "text",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        mediaType: {
                            label: translations.value.mediaType,
                            type: "media",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "media",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "media",
                        },
                        videoSrc: {
                            label: translations.value.videoSrc,
                            type: "media",
                        },
                        videoAutoplay: {
                            label: translations.value.videoAutoplay,
                            type: "media",
                        },
                        videoControls: {
                            label: translations.value.videoControls,
                            type: "media",
                        },
                        videoLoop: {
                            label: translations.value.videoLoop,
                            type: "media",
                        },
                        iframeSrc: {
                            label: translations.value.iframeSrc,
                            type: "media",
                        },
                    },
                    [
                        {
                            label: "Planning",
                            title: "Event Planning",
                            description:
                                "Organize and plan your events with our comprehensive tools for scheduling, venue management, and logistics coordination.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "events-and-tickets",
                                "female-wedding-planner-working-ceremony"
                            ),
                            imageAlt: "Event planning and organization",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Tickets",
                            title: "Ticket Sales",
                            description:
                                "Manage ticket sales, pricing tiers, and attendee registration with our integrated ticketing system.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "events-and-tickets",
                                "diverse-business-people-dinner-party"
                            ),
                            imageAlt: "Ticket sales and management",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Webinars",
                            title: "Virtual Events",
                            description:
                                "Host engaging virtual events and webinars with built-in streaming, interaction tools, and analytics.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "webinars",
                                "team-meeting-online-conference-call-laptop"
                            ),
                            imageAlt: "Virtual events and webinars",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Community",
                            title: "Community Building",
                            description:
                                "Foster connections between attendees and build lasting communities around your events and brand.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "communities",
                                "diversity-teenagers-friends-friendship-team-concept"
                            ),
                            imageAlt: "Community building and networking",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        label: "New Tab",
                        title: "New Tab Title",
                        description: "New tab description",
                        mediaType: MediaType.IMAGE,
                        imageSrc: getStockPhotoUrl(
                            "events-and-tickets",
                            "group-business-people-meeting-standing-grouped-office"
                        ),
                        imageAlt: "New tab image",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const tabsWithUnderlineAndIconsBlock = computed<BlockDef>(() =>
        createBlock<TabsWithUnderlineAndIconsProps>({
            typeId: "TabsWithUnderlineAndIcons",
            type: "tabs",
            name: t("page.blocks.tabs.tabsWithUnderlineAndIcons"),
            component: TabsWithUnderlineAndIcons,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Digital Learning Ecosystem", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Transform your learning experience with our integrated platform that combines courses, resources, and community",
                    translations.value.text
                )
            )
            .withArrayField(
                "tabs",
                arrayField(
                    translations.value.tab,
                    "tabs",
                    {
                        label: {
                            label: translations.value.label,
                            type: "text",
                        },
                        labelIconSrc: {
                            label: translations.value.iconSrc,
                            type: "text",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        mediaType: {
                            label: translations.value.mediaType,
                            type: "media",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "media",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "media",
                        },
                        videoSrc: {
                            label: translations.value.videoSrc,
                            type: "media",
                        },
                        videoAutoplay: {
                            label: translations.value.videoAutoplay,
                            type: "media",
                        },
                        videoControls: {
                            label: translations.value.videoControls,
                            type: "media",
                        },
                        videoLoop: {
                            label: translations.value.videoLoop,
                            type: "media",
                        },
                        iframeSrc: {
                            label: translations.value.iframeSrc,
                            type: "media",
                        },
                    },
                    [
                        {
                            label: "Courses",
                            labelIconSrc: `${ICON_API_BASE_URL}/education/graduation-hat-01.svg`,
                            title: "Interactive Courses",
                            description:
                                "Engage with expertly crafted courses featuring multimedia content, assessments, and hands-on projects.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "courses",
                                "cheerful-young-woman-glasses-front-laptop"
                            ),
                            imageAlt: "Interactive online courses",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "E-Books",
                            labelIconSrc: `${ICON_API_BASE_URL}/files/book-open-01.svg`,
                            title: "Digital Library",
                            description:
                                "Access our vast collection of e-books, research papers, and industry publications for comprehensive learning.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "e-books",
                                "books-stack-with-headphones-arrangement"
                            ),
                            imageAlt: "Digital books and library",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Community",
                            labelIconSrc: `${ICON_API_BASE_URL}/users/users-01.svg`,
                            title: "Learning Community",
                            description:
                                "Connect with fellow learners, share insights, and collaborate on projects in our supportive community.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl("communities", "friends-stacking-hands"),
                            imageAlt: "Learning community and collaboration",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Webinars",
                            labelIconSrc: `${ICON_API_BASE_URL}/communication/video-recorder.svg`,
                            title: "Expert Webinars",
                            description:
                                "Attend live sessions with industry experts and thought leaders to gain cutting-edge insights and knowledge.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "webinars",
                                "woman-work-having-video-call-laptop"
                            ),
                            imageAlt: "Expert webinars and live sessions",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        label: "New Tab",
                        labelIconSrc: `${ICON_API_BASE_URL}/general/plus.svg`,
                        title: "New Tab Title",
                        description: "New tab description",
                        mediaType: MediaType.IMAGE,
                        imageSrc: getStockPhotoUrl(
                            "courses",
                            "teacher-talking-with-her-students-online"
                        ),
                        imageAlt: "New tab image",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const withTabsBlock = computed<BlockDef>(() =>
        createBlock<WithTabsProps>({
            typeId: "WithTabs",
            type: "tabs",
            name: t("page.blocks.tabs.withTabs"),
            component: WithTabs,
        })
            .withField(
                "title",
                headingField(translations.value.title, "SaaS Platform Features", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Discover our comprehensive software platform designed to streamline your workflow and enhance productivity across all aspects of your business operations",
                    translations.value.text
                )
            )
            .withArrayField(
                "tabs",
                arrayField(
                    translations.value.tab,
                    "tabs",
                    {
                        label: {
                            label: translations.value.label,
                            type: "text",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        mediaType: {
                            label: translations.value.mediaType,
                            type: "media",
                        },
                        imageSrc: {
                            label: translations.value.src,
                            type: "media",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "media",
                        },
                        videoSrc: {
                            label: translations.value.videoSrc,
                            type: "media",
                        },
                        videoAutoplay: {
                            label: translations.value.videoAutoplay,
                            type: "media",
                        },
                        videoControls: {
                            label: translations.value.videoControls,
                            type: "media",
                        },
                        videoLoop: {
                            label: translations.value.videoLoop,
                            type: "media",
                        },
                        iframeSrc: {
                            label: translations.value.iframeSrc,
                            type: "media",
                        },
                    },
                    [
                        {
                            label: "Development",
                            title: "Development Tools",
                            description:
                                "Access powerful development environments, code repositories, and collaboration tools designed for modern software teams and agile workflows.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "licences-saas",
                                "cheerful-web-developers-working-laptop"
                            ),
                            imageAlt: "Software development tools and environment",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Analytics",
                            title: "Business Analytics",
                            description:
                                "Gain deep insights into your business performance with advanced analytics, reporting dashboards, and data visualization tools.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "licences-saas",
                                "portrait-male-programmer-office"
                            ),
                            imageAlt: "Business analytics and reporting",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Collaboration",
                            title: "Team Collaboration",
                            description:
                                "Enhance team productivity with integrated communication tools, project management features, and real-time collaboration capabilities.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "webinars",
                                "business-team-negotiating-with-partners"
                            ),
                            imageAlt: "Team collaboration and communication",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                        {
                            label: "Integration",
                            title: "API Integration",
                            description:
                                "Seamlessly connect with your existing tools and services through our robust API ecosystem and pre-built integrations with popular platforms.",
                            mediaType: MediaType.IMAGE,
                            imageSrc: getStockPhotoUrl(
                                "licences-saas",
                                "teamwork-programmer-working-with-computer"
                            ),
                            imageAlt: "API integration and connectivity",
                            videoSrc: defaultVideoUrl,
                            videoAutoplay: false,
                            videoControls: true,
                            videoLoop: false,
                            iframeSrc:
                                "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                        },
                    ],
                    {
                        label: "New Tab",
                        title: "New Tab Title",
                        description: "New tab description",
                        mediaType: MediaType.IMAGE,
                        imageSrc: getStockPhotoUrl(
                            "licences-saas",
                            "cloud-system-tablet-background-smart-technology-remixed-media"
                        ),
                        imageAlt: "New tab image",
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            borderedBarBlock.value,
            fullWidthTabsWithUnderlineBlock.value,
            tabsInPillsBlock.value,
            tabsWithUnderlineAndIconsBlock.value,
            withTabsBlock.value,
        ]),
    };
}
